import React, { createContext, ReactNode, useContext, useRef, useState } from 'react';
import styled from 'styled-components';
import { Alert } from 'react-bootstrap';
import find from 'lodash/find';
import reject from 'lodash/reject';
import { TExceptionType } from 'TProtocol/common/exceptions';

export enum IUErrorLevel {
  INFO = 'success',
  WARNING = 'warning',
  ERROR = 'danger'
}

export interface IUError {
  message: string;
  level: IUErrorLevel;
}

export type ErrorMessage = string | {
  exceptionType: TExceptionType;
  message?: string;
  name?: string;
  reason?: string;
};

interface IUErrorIndexed extends IUError {
  id: number;
}

interface IUErrorContext {
  setErrorMessage: (error: IUError) => void;
  log: (...message: string[]) => void;
  error: (...message: string[]) => void;
  warning: (...message: string[]) => void;
  info: (...message: string[]) => void;
  clearAll: () => void;
}

const ErrorContext = createContext<IUErrorContext | null>(null);

let lastErrorId = 1;

export function ErrorContextProvider(props: { children: ReactNode }) {
  const [errors, setErrors] = useState<IUErrorIndexed[]>([]);

  const ref = useRef<HTMLDivElement>(null);

  const combineMessages = (...messages: ErrorMessage[]): string => {
    return messages.map((message) => {
      if (typeof message === 'string') {
        return message;
      }

      return message.message ?? message.reason ?? message.name ?? message.toString();
    }).join(' ');
  };

  const findErrorMessage = (message: string): IUError | null => {
    return find(errors, { message }) ?? null;
  };

  const setErrorMessage = (error: IUError) => {
    const existingMessage = findErrorMessage(error.message);
    if (existingMessage) {
      // TODO: when expiry is implemented, refresh expiry of existing message.  For now, do nothing.
      return;
    }
    errors.push({
      ...error,
      id: lastErrorId++
    });
    setErrors([...errors]);
  };

  const log = (...messages: ErrorMessage[]) => {
    // eslint-disable-next-line no-console
    console.log(combineMessages(...messages));
  };

  const error = (...messages: ErrorMessage[]) => {
    setErrorMessage({
      message: combineMessages(...messages),
      level: IUErrorLevel.ERROR
    });
  };

  const warning = (...messages: ErrorMessage[]) => {
    setErrorMessage({
      message: combineMessages(...messages),
      level: IUErrorLevel.WARNING
    });
  };

  const info = (...messages: ErrorMessage[]) => {
    setErrorMessage({
      message: combineMessages(...messages),
      level: IUErrorLevel.INFO
    });
  };

  const clearAll = () => {
    setErrors([]);
  };

  const context = {
    setErrorMessage,
    log,
    error,
    warning,
    info,
    clearAll
  };

  const onClose = (id: number) => {
    setErrors(
      reject(errors, { id })
    );
  };

  return <ErrorContext.Provider value={context}>
    <ErrorContainer ref={ref}>
      {
        errors.map((error) => {
          return <Alert key={error.id} variant={error.level} dismissible={true} onClose={() => {
            onClose(error.id);
          }}>
            {error.message}
          </Alert>;
        })
      }
    </ErrorContainer>
    {props.children}
  </ErrorContext.Provider>;
}

const ErrorContainer = styled.div`
  bottom: 0;
  padding-left: 10px;
  position: fixed;
  z-index: 200;
`;

export const useErrorContext = () => {
  const context = useContext(ErrorContext);
  if (context === null) {
    throw new Error('useErrorContext must be used within an ErrorContextProvider');
  }
  return context;
};
